import { Component } from '@theme/component';
import { eventBus } from '@theme/events';
import { CartActions, CartUpdateEvent } from '@theme/cart--state--actions';

/**
 * @typedef {Object} QuantitySelectorRefs
 * @property {HTMLInputElement} input - The number input
 * @property {HTMLButtonElement} decrease - Decrease button
 * @property {HTMLButtonElement} increase - Increase button
 */

/**
 * Quantity Selector Web Component
 *
 * Generic +/- quantity controls. Works in two modes:
 *
 * 1. **Standalone mode** (no data-key): Dispatches `quantity:change` events
 *    for the parent to handle. Use for order quantities, inventory, etc.
 *
 * 2. **Cart mode** (with data-key): Emits CartUpdateEvent (action) for cart sync.
 *    Dispatches `quantity:remove` when quantity reaches min (typically 0).
 *
 * Architecture (ngrx-style, cart mode only):
 * - Emits CartUpdateEvent (action) to request quantity change
 * - Listens to cart:updated (result) to confirm change
 * - Listens to cart:update-failed (result) to rollback
 *
 * @attr {number} data-value - Current value
 * @attr {number} [data-min=0] - Minimum allowed value
 * @attr {number} [data-max] - Maximum allowed value (omit for no cap)
 * @attr {string} [data-key] - Cart line item key (enables cart mode)
 * @attr {string} [data-state] - Cart mode state: '' | 'loading' (set during pending update)
 *
 * @fires quantity:change - Fired on value change (both modes)
 * @fires quantity:remove - Fired when value reaches min in cart mode
 *
 * @example Standalone
 * <quantity-selector data-value="1" data-min="1" data-max="10">
 *   <button ref="decrease" on:click="/handleDecrease">−</button>
 *   <input ref="input" on:change="/handleChange" type="number" value="1" min="1" max="10">
 *   <button ref="increase" on:click="/handleIncrease">+</button>
 * </quantity-selector>
 *
 * @example Cart item
 * <quantity-selector data-key="abc123:def456" data-value="2">
 *   ...
 * </quantity-selector>
 *
 * @extends {Component<QuantitySelectorRefs>}
 */
class QuantitySelector extends Component {
	/** @type {number|null} */
	#debounceTimer = null;

	/** @type {string|null} Pending key for cart mode */
	#pendingKey = null;

	/** @type {number|null} Previous value for rollback */
	#previousValue = null;

	/** @type {(() => void)[]} */
	#unsubscribes = [];

	/** @type {number|null} */
	#maxAttemptTimer = null;

	/** @type {number} */
	get #min() {
		const value = this.dataset.min;
		if (value == null || value === '') return 0;
		const parsed = parseInt(value, 10);
		return Number.isNaN(parsed) ? 0 : parsed;
	}

	/** @type {number|null} */
	get #max() {
		const value = this.dataset.max;
		if (value == null || value === '') return null;
		const parsed = parseInt(value, 10);
		return Number.isNaN(parsed) ? null : parsed;
	}

	/** @type {boolean} */
	get #isCartMode() {
		return Boolean(this.dataset.key);
	}

	connectedCallback() {
		super.connectedCallback();
		this.#updateButtonStates();

		// Cart mode: listen for result events
		if (this.#isCartMode) {
			this.#setupCartModeListeners();
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.#debounceTimer) {
			clearTimeout(this.#debounceTimer);
			this.#debounceTimer = null;
		}
		if (this.#maxAttemptTimer) {
			clearTimeout(this.#maxAttemptTimer);
			this.#maxAttemptTimer = null;
		}
		this.#pendingKey = null;
		this.#previousValue = null;

		for (const unsub of this.#unsubscribes) {
			unsub();
		}
		this.#unsubscribes = [];
	}

	/**
	 * Set up listeners for cart mode result events.
	 */
	#setupCartModeListeners() {
		const key = this.dataset.key;

		// Success: confirm value change
		this.#unsubscribes.push(
			eventBus.on(CartActions.updated, (event) => {
				if (this.#pendingKey !== key) return;

				// Skip events for other keys (e.g., concurrent updates from other selectors)
				const updatedKey = event.detail?.data?.key;
				if (updatedKey && updatedKey !== key) return;

				// Update confirmed — clear pending state
				this.dataset.state = '';
				const { input } = this.refs;
				if (input) {
					this.dataset.value = input.value;
				}
				this.#pendingKey = null;
				this.#previousValue = null;
			})
		);

		// Failure: rollback to previous value
		this.#unsubscribes.push(
			eventBus.on(CartActions.updateFailed, (event) => {
				const failedKey = event.detail?.data?.key;
				if (failedKey === key && this.#previousValue !== null) {
					// Revert input to previous value
					this.dataset.state = '';
					const { input } = this.refs;
					if (input) {
						input.value = String(this.#previousValue);
					}
					this.#updateButtonStates();
					this.#pendingKey = null;
					this.#previousValue = null;
				}
			})
		);
	}

	/**
	 * Handle decrease button click — bound via on:click="/handleDecrease"
	 */
	handleDecrease() {
		const { input } = this.refs;
		if (!input) return;

		const current = parseInt(input.value, 10) || 0;
		const next = Math.max(this.#min, current - 1);
		if (next === current) return;

		input.value = String(next);
		this.#scheduleUpdate(next);
		this.#updateButtonStates();
	}

	/**
	 * Handle increase button click — bound via on:click="/handleIncrease"
	 */
	handleIncrease() {
		const { input } = this.refs;
		if (!input) return;

		const current = parseInt(input.value, 10) || 0;
		let next = current + 1;
		if (this.#max != null && next > this.#max) next = this.#max;
		if (next === current) {
			this.#flagMaxAttempt();
			return;
		}

		input.value = String(next);
		this.#scheduleUpdate(next);
		this.#updateButtonStates();
	}

	/**
	 * Briefly flags that the shopper tried to increase past the max (e.g. to
	 * reveal a "No more units available" notice) — the + button stays
	 * clickable at max (not a native disabled) specifically so this attempt
	 * can be caught, instead of the browser silently swallowing the click.
	 */
	#flagMaxAttempt() {
		this.dataset.maxAttempt = 'true';
		if (this.#maxAttemptTimer) clearTimeout(this.#maxAttemptTimer);
		this.#maxAttemptTimer = setTimeout(() => {
			this.#maxAttemptTimer = null;
			delete this.dataset.maxAttempt;
		}, 2000);
	}

	/**
	 * Handle input change — bound via on:change="/handleChange"
	 * @param {Event} event
	 */
	handleChange(event) {
		const input = event.target;
		let next = Math.max(this.#min, parseInt(input.value, 10) || 0);
		if (this.#max != null) next = Math.min(next, this.#max);
		input.value = String(next);
		this.#scheduleUpdate(next);
		this.#updateButtonStates();
	}

	/**
	 * Enable/disable +/− buttons based on current value, min, and max.
	 */
	#updateButtonStates() {
		const { input, decrease, increase } = this.refs;
		if (!input) return;

		const value = parseInt(input.value, 10) || 0;

		if (decrease) {
			decrease.disabled = value <= this.#min;
		}
		// Not a native disabled — [data-at-max] styles it as looking disabled
		// (see shared--basic--quantity-selector.liquid), but it must stay
		// clickable so #flagMaxAttempt can catch the attempt to go higher.
		// aria-disabled (not the disabled attribute) keeps it announced as
		// disabled to assistive tech without blocking focus/activation.
		const atMax = this.#max != null && value >= this.#max;
		if (increase) {
			increase.setAttribute('aria-disabled', String(atMax));
		}
		this.toggleAttribute('data-at-max', atMax);
	}

	/**
	 * Debounce quantity updates to handle rapid clicks.
	 * @param {number} value
	 */
	#scheduleUpdate(value) {
		if (this.#debounceTimer) {
			clearTimeout(this.#debounceTimer);
		}

		this.#debounceTimer = setTimeout(() => {
			this.#debounceTimer = null;
			this.#commitValue(value);
		}, 300);
	}

	/**
	 * Commit the value change.
	 * In cart mode: emits CartUpdateEvent (action).
	 * In standalone mode: dispatches event for parent.
	 * @param {number} value
	 */
	#commitValue(value) {
		const previousValue = parseInt(this.dataset.value, 10) || 0;

		// Always dispatch change event for parent components
		this.dispatchEvent(new CustomEvent('quantity:change', {
			bubbles: true,
			detail: { value, previousValue },
		}));

		// Cart mode: emit action event
		if (this.#isCartMode) {
			const key = this.dataset.key;

			// Track for rollback
			this.#pendingKey = key;
			this.#previousValue = previousValue;
			this.dataset.state = 'loading';

			// Emit action: request to update quantity
			eventBus.emit(new CartUpdateEvent({
				data: { key, quantity: value },
			}));

			// Item removed — notify parent components so they can handle row removal
			if (value === this.#min) {
				this.dispatchEvent(new CustomEvent('quantity:remove', {
					bubbles: true,
					detail: { key },
				}));
			}
		} else {
			// Standalone mode: just update the data attribute
			this.dataset.value = String(value);
		}
	}
}

if (!customElements.get('quantity-selector')) {
	customElements.define('quantity-selector', QuantitySelector);
}
